import { Component } from 'react';
import { reportError } from '@/utils/debugReporter';

// Detect a chunk-load failure (happens when a new deploy invalidates old chunk hashes).
function isChunkLoadError(error) {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();
  return (
    name === 'chunkloaderror' ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunk: false, reported: false, reporting: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      const key = 'vroum_chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return;
      }
    }
    // Auto-report to Telegram (silent)
    reportError(`React crash: ${error.message}`, {
      component: info.componentStack?.slice(0, 300),
    });
  }

  handleSendReport = async () => {
    this.setState({ reporting: true });
    try {
      await reportError(`[USER REPORT] React crash: ${this.state.error?.message || 'unknown'}`, {
        url: window.location.href,
        userAgent: navigator.userAgent.slice(0, 120),
        stack: this.state.error?.stack?.slice(0, 500) || '',
        userTriggered: true,
      });
    } catch { /* silent */ }
    this.setState({ reporting: false, reported: true });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunk) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#08070b' }}>
            <div className="text-center">
              <div className="animate-spin w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-white/60 text-sm">Ανανέωση σελίδας…</p>
            </div>
          </div>
        );
      }

      const { reported, reporting } = this.state;

      return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#08070b' }}>
          <div className="rounded-2xl shadow-lg p-8 max-w-md w-full text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-white mb-2">Κάτι πήγε στραβά</h1>
            <p className="text-white/50 text-sm mb-6">
              Συνέβη ένα απρόσμενο σφάλμα. Πάτα ανανέωση ή στείλε αναφορά για να το διορθωθεί γρήγορα.
            </p>

            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-400 transition-colors mb-3"
            >
              🔄 Ανανέωσε τη σελίδα
            </button>

            {!reported ? (
              <button
                onClick={this.handleSendReport}
                disabled={reporting}
                className="w-full py-3 bg-white/[0.06] border border-white/10 text-white/70 rounded-xl font-medium hover:bg-white/10 transition-colors text-sm disabled:opacity-50"
              >
                {reporting ? '⏳ Αποστολή…' : '📩 Στείλε αναφορά στον Γιάννη να το διορθώσει'}
              </button>
            ) : (
              <div className="w-full py-3 bg-green-500/15 border border-green-400/20 text-green-400 rounded-xl text-sm font-medium">
                ✅ Η αναφορά στάλθηκε! Θα το κοιτάξουμε σύντομα.
              </div>
            )}

            {process.env.NODE_ENV === 'development' && (
              <pre className="mt-4 text-left text-xs text-red-400 bg-red-900/20 p-3 rounded-lg overflow-auto max-h-40">
                {this.state.error?.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
